"use client"

import type React from "react"
import { Box, Typography, IconButton, LinearProgress, Chip } from "@mui/material"
import { FileUpload, CheckCircle, Edit } from "@mui/icons-material"

interface FileUploaderProps {
  file: File | null
  fileUploadStatus: "idle" | "uploading" | "success" | "error"
  uploadProgress: number
  formSubmitted: boolean
  onFileUpload: (event: React.DragEvent<HTMLDivElement> | React.ChangeEvent<HTMLInputElement>) => void
  onRemoveFile: () => void
}

/**
 * Component for handling file uploads with drag-and-drop support
 */
export const FileUploader = ({
  file,
  fileUploadStatus,
  uploadProgress,
  formSubmitted,
  onFileUpload,
  onRemoveFile,
}: FileUploaderProps) => {
  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
  }

  if (fileUploadStatus === "idle") {
    return (
      <Box
        sx={{
          border: `2px dashed ${formSubmitted && !file ? "#d32f2f" : "#E8E8E8"}`,
          borderRadius: 1,
          p: 4,
          textAlign: "center",
          cursor: "pointer",
          "&:hover": { borderColor: formSubmitted && !file ? "#d32f2f" : "#2064B4" },
        }}
        onDrop={onFileUpload}
        onDragOver={handleDragOver}
        onClick={() => document.getElementById("file-upload")?.click()}
      >
        <input id="file-upload" type="file" hidden onChange={onFileUpload} />
        <FileUpload sx={{ fontSize: 40, color: formSubmitted && !file ? "#d32f2f" : "#666666", mb: 1 }} />
        <Typography sx={{ color: formSubmitted && !file ? "#d32f2f" : "#666666" }}>
          Drop files here
          <br />
          or <span style={{ color: formSubmitted && !file ? "#d32f2f" : "#2064B4" }}>click to upload</span>
        </Typography>
        {formSubmitted && !file && (
          <Typography variant="caption" color="error" sx={{ display: "block", mt: 1 }}>
            File upload is required
          </Typography>
        )}
      </Box>
    )
  }

  return (
    <Box sx={{ mt: 2 }}>
      {fileUploadStatus === "uploading" && (
        <Box sx={{ width: "100%", mb: 2 }}>
          <LinearProgress
            variant="determinate"
            value={uploadProgress}
            sx={{
              height: 8,
              borderRadius: 4,
              "& .MuiLinearProgress-bar": { backgroundColor: "#478B1A" },
            }}
          />
          <Typography sx={{ mt: 1, color: "#666666", textAlign: "center" }}>Uploading... {uploadProgress}%</Typography>
        </Box>
      )}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          p: 2,
          border: "1px solid #E8E8E8",
          borderRadius: 1,
          position: "relative",
          ...(fileUploadStatus === "success" && {
            borderColor: "#478B1A",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "4px",
              backgroundColor: "#478B1A",
              borderTopLeftRadius: "4px",
              borderTopRightRadius: "4px",
            },
          }),
        }}
      >
        {fileUploadStatus === "success" && (
          <Chip
            label="Success"
            color="success"
            size="small"
            sx={{
              position: "absolute",
              top: -10,
              right: 16,
              backgroundColor: "#478B1A",
              color: "white",
              fontWeight: "bold",
            }}
          />
        )}
        <CheckCircle sx={{ color: "#478B1A", mr: 2 }} />
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            {file?.name || "[File Name].zip"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {file ? `${(file.size / 1024).toFixed(0)}KB` : "150KB"}
          </Typography>
        </Box>
        <IconButton onClick={onRemoveFile} size="small">
          <Edit sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>
    </Box>
  )
}
